export interface GoogleAddress {
  formattedAddress: string;
  addressComponents: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  location: {
    lat: number;
    lng: number;
  };
  raw: google.maps.places.PlaceResult; // Keep the raw Google result for flexibility
}

// AutocompleteAddress.tsx
import { useEffect, useState, useCallback, useRef } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { logger } from "@/services/logger";

interface Props {
  onSelectedAddress: (address: GoogleAddress | null) => void;
  placeholder?: string;
  initialValue?: string;
  initialAddressComponents?: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  showEditFormByDefault?: boolean;
  readOnly?: boolean;
  showFullForm?: boolean;
  onlyShowAutocomplete?: boolean;
}

export function AutocompleteAddress({
  onSelectedAddress,
  placeholder,
  initialValue,
  initialAddressComponents,
  showEditFormByDefault = false,
  readOnly = false,
  showFullForm = false,
  onlyShowAutocomplete = false,
}: Props) {
  const map = useMap();
  const places = useMapsLibrary("places");

  const [isOpen, setIsOpen] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<GoogleAddress | null>(
    null
  );
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  // Services
  const [sessionToken, setSessionToken] =
    useState<google.maps.places.AutocompleteSessionToken>();
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] =
    useState<google.maps.places.PlacesService | null>(null);

  // Form state
  const [editableAddress, setEditableAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
  });

  // Add a ref to track if we're interacting with the edit form
  const editFormRef = useRef<HTMLDivElement>(null);

  // Add state to track if we're editing
  const [isEditing, setIsEditing] = useState(false);

  // Add postal code formatting function
  const formatPostalCode = (value: string): string => {
    // Remove all non-alphanumeric characters and convert to uppercase
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

    // Canadian postal code format: A1A 1A1
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    } else {
      // Limit to 6 characters max
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`;
    }
  };

  // Handle postal code change with formatting
  const handlePostalCodeChange = (value: string) => {
    const formatted = formatPostalCode(value);
    setEditableAddress((prev) => ({
      ...prev,
      postalCode: formatted,
    }));
    if (selectedPlace) {
      onSelectedAddress({
        ...selectedPlace,
        addressComponents: {
          ...selectedPlace.addressComponents,
          postalCode: formatted,
        },
      });
    }
  };

  // Initialize Google services
  useEffect(() => {
    if (!places || !map) return;

    try {
      const autocomplete = new places.AutocompleteService();
      const placesServ = new places.PlacesService(map);
      const token = new places.AutocompleteSessionToken();

      setAutocompleteService(autocomplete);
      setPlacesService(placesServ);
      setSessionToken(token);
    } catch (error) {
      logger.error("Error initializing services:", error);
    }
  }, [map, places]);

  // Handle predictions fetch
  const fetchPredictions = useCallback(
    async (input: string) => {
      logger.info("Fetching predictions for:", !input, !autocompleteService);
      if (!autocompleteService || !input) {
        setPredictions([]);
        return;
      }

      try {
        const response = await autocompleteService.getPlacePredictions({
          input,
          sessionToken,
          componentRestrictions: { country: "ca" },
          region: "CA",
          language: "en",
        });

        // Add debug logging
        logger.info("Predictions response:", response);

        setPredictions(response?.predictions || []);
      } catch (error) {
        logger.error("Error fetching predictions:", error);
        setPredictions([]);
      }
    },
    [autocompleteService, sessionToken]
  );

  // Handle place selection
  const handlePlaceSelect = useCallback(
    async (placeId: string) => {
      if (!placesService || !places) return;

      try {
        await new Promise((resolve, reject) => {
          placesService.getDetails(
            {
              placeId,
              fields: ["address_components", "geometry", "formatted_address"],
              sessionToken,
              language: "en",
              region: "CA",
            },
            (result, status) => {
              if (
                status === google.maps.places.PlacesServiceStatus.OK &&
                result
              ) {
                // Check for subpremise (unit/apartment number) in address components
                const subpremise = result.address_components?.find(
                  (component) => component.types.includes("subpremise")
                )?.long_name;

                const address: GoogleAddress = {
                  formattedAddress: result.formatted_address ?? "",
                  addressComponents: {
                    line1: "",
                    line2: subpremise ?? "",
                    city: "",
                    state: "",
                    country: "",
                    postalCode: "",
                  },
                  location: {
                    lat: result.geometry?.location?.lat() ?? 0,
                    lng: result.geometry?.location?.lng() ?? 0,
                  },
                  raw: result,
                };

                // Rest of the address parsing...
                const streetNumber =
                  result.address_components?.find((component) =>
                    component.types.includes("street_number")
                  )?.long_name ?? "";

                const route =
                  result.address_components?.find((component) =>
                    component.types.includes("route")
                  )?.long_name ?? "";

                address.addressComponents.line1 =
                  `${streetNumber} ${route}`.trim();

                // Parse other components as before...
                result.address_components?.forEach((component) => {
                  const types = component.types;
                  if (types.includes("locality")) {
                    address.addressComponents.city = component.long_name;
                  }
                  if (types.includes("administrative_area_level_1")) {
                    address.addressComponents.state = component.long_name;
                  }
                  if (types.includes("country")) {
                    address.addressComponents.country =
                      component.short_name === "CA"
                        ? "CA"
                        : component.short_name;
                  }
                  if (types.includes("postal_code")) {
                    address.addressComponents.postalCode = component.long_name;
                  }
                });

                setSelectedPlace(address);
                setEditableAddress(address.addressComponents);
                setShowEditForm(true);
                setIsEditing(true);
                onSelectedAddress(address);
                setSessionToken(new places.AutocompleteSessionToken());
                resolve(address);
              } else {
                reject(new Error(`Place details failed: ${status}`));
              }
            }
          );
        });
      } catch (error) {
        logger.error("Error fetching place details:", error);
      }
    },
    [onSelectedAddress, places, placesService, sessionToken]
  );

  const handleReset = useCallback(() => {
    setSelectedPlace(null);
    setShowEditForm(false);
    setInputValue("");
    setEditableAddress({
      line1: "",
      line2: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    });
    onSelectedAddress(null);
  }, [onSelectedAddress]);

  // Add this useEffect to handle initial value
  useEffect(() => {
    if (initialValue) {
      setInputValue(initialValue);
    }
  }, [initialValue]);

  // Helper function to format address
  const getFormattedAddress = (components: typeof editableAddress) => {
    const line1 = components.line1;
    const line2 = components.line2;

    // For city and province, combine them properly
    let cityProvince = "";
    if (components.city && components.state) {
      cityProvince = `${components.city}, ${components.state}`;
    } else if (components.city) {
      cityProvince = components.city;
    } else if (components.state) {
      cityProvince = components.state;
    }

    // Add postal code if available
    const cityProvPostal = components.postalCode
      ? `${cityProvince} ${components.postalCode}`
      : cityProvince;

    // Include country in the formatted address
    const country = components.country || "CA";

    // Build the parts array with only non-empty values
    const parts = [line1, line2, cityProvPostal, country].filter(Boolean);

    return parts.join(", ");
  };

  // Update initialization effect
  useEffect(() => {
    if (initialAddressComponents) {
      setEditableAddress(initialAddressComponents);
      setShowEditForm(showEditFormByDefault);
      setSelectedPlace({
        formattedAddress:
          initialValue ?? getFormattedAddress(initialAddressComponents),
        addressComponents: initialAddressComponents,
        location: { lat: 0, lng: 0 },
        raw: {} as google.maps.places.PlaceResult,
      });
    }
  }, [initialAddressComponents, initialValue, showEditFormByDefault]);

  return (
    <div className="space-y-4">
      {selectedPlace ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Input value={getFormattedAddress(editableAddress)} readOnly />
            {!readOnly && (
              <>
                {!onlyShowAutocomplete && (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      setShowEditForm(!showEditForm);
                      setIsEditing(!isEditing);
                    }}
                  >
                    {showEditForm ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                )}
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    handleReset();
                    setIsEditing(false);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>

          {(showFullForm || (showEditForm && !readOnly)) &&
            !onlyShowAutocomplete && (
              <div className="space-y-4 border rounded-lg p-4 bg-background">
                <div className="space-y-4">
                  {!readOnly && (
                    <div className="text-sm text-muted-foreground bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="font-medium text-blue-900 mb-1">
                        Address Editing
                      </p>
                      <p className="text-blue-800">
                        You can edit missing fields or add additional details.
                        Postal code is required for accurate shipping.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Address Line 2 (Unit/Suite)</Label>
                    <Input
                      value={editableAddress.line2}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setEditableAddress((prev) => ({
                          ...prev,
                          line2: newValue,
                        }));
                        if (selectedPlace) {
                          onSelectedAddress({
                            ...selectedPlace,
                            addressComponents: {
                              ...selectedPlace.addressComponents,
                              line2: newValue,
                            },
                          });
                        }
                      }}
                      placeholder="Apartment, suite, unit, etc."
                      disabled={readOnly}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Address Line 1</Label>
                    <Input
                      value={editableAddress.line1}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setEditableAddress((prev) => ({
                          ...prev,
                          line1: newValue,
                        }));
                        if (selectedPlace) {
                          onSelectedAddress({
                            ...selectedPlace,
                            addressComponents: {
                              ...selectedPlace.addressComponents,
                              line1: newValue,
                            },
                          });
                        }
                      }}
                      placeholder="Street address"
                      disabled={
                        readOnly ||
                        !!(
                          editableAddress.line1 &&
                          editableAddress.line1.trim() !== ""
                        )
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={editableAddress.city}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setEditableAddress((prev) => ({
                            ...prev,
                            city: newValue,
                          }));
                          if (selectedPlace) {
                            onSelectedAddress({
                              ...selectedPlace,
                              addressComponents: {
                                ...selectedPlace.addressComponents,
                                city: newValue,
                              },
                            });
                          }
                        }}
                        placeholder="City"
                        disabled={
                          readOnly ||
                          !!(
                            editableAddress.city &&
                            editableAddress.city.trim() !== ""
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State/Province</Label>
                      <Input
                        value={editableAddress.state}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setEditableAddress((prev) => ({
                            ...prev,
                            state: newValue,
                          }));
                          if (selectedPlace) {
                            onSelectedAddress({
                              ...selectedPlace,
                              addressComponents: {
                                ...selectedPlace.addressComponents,
                                state: newValue,
                              },
                            });
                          }
                        }}
                        placeholder="Province/State"
                        disabled={
                          readOnly ||
                          !!(
                            editableAddress.state &&
                            editableAddress.state.trim() !== ""
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Postal Code</Label>
                      <Input
                        value={editableAddress.postalCode}
                        onChange={(e) => {
                          handlePostalCodeChange(e.target.value);
                        }}
                        placeholder="A1A 1A1"
                        disabled={readOnly}
                        maxLength={7} // A1A 1A1 = 7 characters including space
                        className={
                          !editableAddress.postalCode ||
                          editableAddress.postalCode.trim() === ""
                            ? "border-amber-300 bg-amber-50"
                            : ""
                        }
                      />
                      {(!editableAddress.postalCode ||
                        editableAddress.postalCode.trim() === "") && (
                        <p className="text-xs text-amber-600">
                          Please enter postal code for accurate shipping
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Input
                        value={editableAddress.country}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setEditableAddress((prev) => ({
                            ...prev,
                            country: newValue,
                          }));
                          if (selectedPlace) {
                            onSelectedAddress({
                              ...selectedPlace,
                              addressComponents: {
                                ...selectedPlace.addressComponents,
                                country: newValue,
                              },
                            });
                          }
                        }}
                        placeholder="Country"
                        disabled={
                          readOnly ||
                          !!(
                            editableAddress.country &&
                            editableAddress.country.trim() !== ""
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                {!showFullForm && !readOnly && (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowEditForm(false);
                        setIsEditing(false);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                )}
              </div>
            )}
        </div>
      ) : (
        <div className="relative">
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              void fetchPredictions(e.target.value);
            }}
            onBlur={(e) => {
              if (!editFormRef.current?.contains(e.relatedTarget as Node)) {
                setTimeout(() => {
                  setPredictions([]);
                  setIsOpen(false);
                }, 200);
              }
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={readOnly}
          />
          {isOpen && predictions.length > 0 && !readOnly && (
            <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg">
              {predictions.map((prediction) => (
                <div
                  key={prediction.place_id}
                  className="px-4 py-2 hover:bg-muted cursor-pointer"
                  onClick={() => {
                    void handlePlaceSelect(prediction.place_id);
                    setIsOpen(false);
                    setPredictions([]);
                  }}
                >
                  {prediction.description}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
